import { keyBy, mapValues, omit, pick, uniqueId } from "lodash";
import { Reducer } from "react";

export const enum Actions {
  ChangeFileCategory,
  RemoveFile,
  ResetSelection,
  S3UploadResponse,
  SelectFiles,
  StartPreflight,
}

export const enum HTTPStatus {
  Loading,
  Error,
  Success,
}

export interface Document {
  id: string;
  file: File;
  category: string | null;
  httpStatus: HTTPStatus | null;
  validation: string | null;
}

export type State = {
  [key: string]: Document;
}

interface StandardAction<T, P> {
  type: T;
  payload: P;
}

export type ActionTypes =
  | StandardAction<Actions.ChangeFileCategory, { id: string; category: string }>
  | StandardAction<Actions.RemoveFile, { id: string }>
  | StandardAction<Actions.ResetSelection, Record<string, never>>
  | StandardAction<Actions.S3UploadResponse, { id: string; response: Response }>
  | StandardAction<Actions.SelectFiles, { files: File[] }>
  | StandardAction<Actions.StartPreflight, { documents: string[] }>;

// Returns an HTTPSTatus from the given standard Response object.
const deriveStatus = (response: Response): HTTPStatus => {
  switch (response.status) {
    case 200:
    case 204:
      return HTTPStatus.Success;
    default:
      return HTTPStatus.Error;
  }
};

// Reducer that manages the local state of selecting documents to be uploaded
// to the server.
export const reducer: Reducer<State, ActionTypes> = (docs, action): State => {
  const update = (id: string, updates: Partial<Document>): State =>
    ({ ...docs, [id]: { ...docs[id], ...updates } });

  switch (action.type) {
    case Actions.ChangeFileCategory:
      return update(action.payload.id, {
        category: action.payload.category === "" ? null : action.payload.category,
      });

    case Actions.RemoveFile:
      return omit(docs, action.payload.id);

    case Actions.ResetSelection:
      return {};

    case Actions.S3UploadResponse:
      return update(action.payload.id, { httpStatus: deriveStatus(action.payload.response) });

    case Actions.SelectFiles: {
      const { files } = action.payload;
      const documents = files.map((file) => ({
        id: uniqueId(),
        file,
        category: null,
        httpStatus: null,
        validation: null,
      }));

      return { ...docs, ...keyBy(documents, (doc) => doc.id) };
    }

    case Actions.StartPreflight:
      return {
        ...docs,
        ...mapValues(pick(docs, action.payload.documents), (doc) => ({
          ...doc,
          httpStatus: HTTPStatus.Loading,
        })),
      };

    default:
      return docs;
  }
};
